import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
  DestroyRef,
  effect,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatomoService } from '../../../root-services/matomo.service';
import { ConsentStorageService } from '../../../root-services/consent-storage.service';

/**
 * Cookie Consent Banner Component
 *
 * Displays a non-blocking banner on first visit with options to:
 * - Accept analytics cookies
 * - Reject analytics cookies
 *
 * Story 6.2: Consent UX + Consent-Gated Tracking
 * AC1-4: Banner UX requirements
 */
@Component({
  selector: 'app-cookie-consent-banner',
  imports: [MatButtonModule, TranslatePipe],
  templateUrl: './cookie-consent-banner.component.html',
  styleUrl: './cookie-consent-banner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieConsentBannerComponent implements OnInit {
  private readonly matomoService = inject(MatomoService);
  private readonly consentStorage = inject(ConsentStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  /**
   * Banner visibility state
   */
  visible = signal<boolean>(false);

  constructor() {
    // Watch consent changes from anywhere (banner buttons or preferences dialog)
    effect(() => {
      const consent = this.consentStorage.consent();
      if (consent !== null) {
        this.visible.set(false);
      }
    });
  }

  ngOnInit(): void {
    // Wait for translations to load before showing banner
    // This ensures the banner shows in the correct language on page refresh
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkBannerVisibility();
      });

    // Also check immediately in case language is already loaded
    if (this.translateService.getCurrentLang()) {
      this.checkBannerVisibility();
    }
  }

  /**
   * Check if banner should be visible and initialize consent
   * @private
   */
  private checkBannerVisibility(): void {
    // Initialize consent state from storage
    this.initializeConsent();

    // Show banner if no consent choice has been made
    if (this.consentStorage.getConsent() === null) {
      this.visible.set(true);
    }
  }

  /**
   * Initialize consent state from localStorage
   * AC9: If user clears storage, treat as not consented
   */
  private initializeConsent(): void {
    const storedConsent = this.consentStorage.getConsent();

    if (storedConsent === 'accepted') {
      // Check if Matomo consent cookie exists
      const hasMatomoConsentCookie = this.hasMatomoConsentCookie();

      // AC10: If Matomo consent cookie is missing but app has "accepted" preference,
      // do not silently re-consent - prompt again
      if (!hasMatomoConsentCookie) {
        this.consentStorage.clearConsent();
        this.visible.set(true);
        return;
      }

      // Valid consent state - give consent
      this.matomoService.giveConsent();
    }
  }

  /**
   * Check if Matomo consent cookie exists
   * Matomo sets 'mtm_consent' or 'mtm_consent_removed' cookies
   * Uses exact key matching to prevent false positives (e.g., mtm_consent_extra)
   */
  private hasMatomoConsentCookie(): boolean {
    try {
      // Check if document.cookie is accessible (SSR-safe)
      if (
        this.document === undefined ||
        typeof this.document.cookie !== 'string'
      ) {
        return false;
      }

      const cookieString = this.document.cookie;
      if (!cookieString) {
        return false;
      }

      // Parse cookies into key-value map for exact matching
      const cookieMap = cookieString.split(';').reduce((map, cookie) => {
        const [key, value] = cookie.trim().split('=');
        if (key) {
          map.set(key, value || '');
        }
        return map;
      }, new Map<string, string>());

      // Exact key matching prevents false positives
      const hasConsent = cookieMap.has('mtm_consent');
      const hasRemoved = cookieMap.has('mtm_consent_removed');

      return hasConsent && !hasRemoved;
    } catch {
      return false;
    }
  }

  /**
   * Accept analytics cookies
   * AC6: Enable tracking via consent API
   */
  acceptAnalytics(): void {
    this.consentStorage.setConsent('accepted');
    this.matomoService.giveConsent();
    this.visible.set(false);
  }

  /**
   * Reject analytics cookies
   * AC7: Tracking remains disabled, persist rejection
   */
  rejectAnalytics(): void {
    this.consentStorage.setConsent('denied');
    this.visible.set(false);
  }
}
